import React, { useState, useEffect, useCallback } from 'react';
import { Stage, Layer, Rect, Text } from 'react-konva';
import Grid from '../Grid.jsx';
import TableModal from '../../pages/Table/TableModal.jsx';
import { jwtDecode } from 'jwt-decode';
import { capitalize } from '../../utils/StringUtils.jsx';
import 'bootstrap/dist/css/bootstrap.min.css';
import '../../styles/TafelOpstelling.css';
import axiosInstance from "../../api/axiosConfig.jsx";

const TafelOpstelling = () => {
    const [tables, setTables] = useState([]);
    const [tableId, setTableId] = useState(1);
    const [loading, setLoading] = useState(true);
    const [modalOpen, setModalOpen] = useState(false);
    const [modalTable, setModalTable] = useState(null);
    const [fetchedCompanyName, setFetchedCompanyName] = useState('');
    const [rooms, setRooms] = useState([]);
    const [selectedRoom, setSelectedRoom] = useState('');
    const [scale, setScale] = useState(1);

    const gridSize = 35;
    const rows = 15;
    const columns = 25;
    const width = gridSize * columns;
    const height = gridSize * rows;

    const updateScale = () => {
        const containerWidth = window.innerWidth * 0.9;
        const containerHeight = window.innerHeight * 0.9;
        const scaleWidth = containerWidth / width;
        const scaleHeight = containerHeight / height;
        const newScale = Math.min(scaleWidth, scaleHeight);
        setScale(newScale);
    };

    useEffect(() => {
        updateScale();
        window.addEventListener('resize', updateScale);
        return () => window.removeEventListener('resize', updateScale);
    }, []);

    const fetchTables = async () => {
        const authToken = sessionStorage.getItem('authToken');
        const decodedToken = jwtDecode(authToken);
        const companyName = decodedToken.companyName;
        setFetchedCompanyName(companyName);

        try {
            const response = await axiosInstance.get(`/tables/company/${companyName}`);
            const fetchedTables = response.data;
            setTables(fetchedTables);
            if (fetchedTables.length > 0) {
                setTableId(Math.max(...fetchedTables.map(table => table.tableId)) + 1);
            }

            const uniqueRooms = [...new Set(fetchedTables.map(table => capitalize(table.room)))];
            setRooms(uniqueRooms);
            if (uniqueRooms.length > 0) {
                setSelectedRoom(uniqueRooms[0]);
            }
        } catch (error) {
            console.error('Error fetching table data:', error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchTables();
    }, []);

    const handleStageClick = (e) => {
        if (modalOpen) return;

        const stage = e.target.getStage();
        const pointerPosition = stage.getPointerPosition();

        setModalTable({
            tableId: tableId,
            seats: 4,
            companyName: fetchedCompanyName,
            x: Math.floor(pointerPosition.x / gridSize) * gridSize,
            y: Math.floor(pointerPosition.y / gridSize) * gridSize,
            tableNumber: '',
            room: '',
        });

        setModalOpen(true);
    };

    const handleTableAdded = () => {
        fetchTables();
        setModalOpen(false);
    };

    const availableSpaces = (rows * columns) - tables.length;
    const filteredTables = tables.filter(table => capitalize(table.room) === selectedRoom);

    return (
        <div className="container mt-5">
            <h1 className="text-center mb-4">Tafelopstelling</h1>

            <div className="mb-3 row justify-content-center">
                <div className="col-auto">
                    <label htmlFor="room-selector" className="col-form-label">Kies een kamer:</label>
                </div>
                <div className="col-auto">
                    <select
                        id="room-selector"
                        value={selectedRoom}
                        onChange={(e) => setSelectedRoom(e.target.value)}
                        className="form-select"
                    >
                        {rooms.map(room => (
                            <option key={room} value={room}>{room}</option>
                        ))}
                    </select>
                </div>
            </div>

            <div className="d-flex justify-content-center">
                <div className="stage-container" style={{ overflow: 'hidden' }}>
                    <Stage
                        width={width * scale}
                        height={height * scale}
                        scaleX={scale}
                        scaleY={scale}
                        onClick={handleStageClick}
                    >
                        <Layer>
                            <Grid gridSize={gridSize} width={width} height={height} color="#ddd" />
                            {filteredTables.map((table) => (
                                <React.Fragment key={table.tableId}>
                                    <Rect
                                        x={table.x}
                                        y={table.y}
                                        width={gridSize}
                                        height={gridSize}
                                        fill="blue"
                                        shadowBlur={5}
                                        className="table-rect"
                                        onClick={(e) => {
                                            e.cancelBubble = true;
                                        }}
                                    />
                                    <Text
                                        text={`Tafel ${table.tableNumber}`}
                                        x={table.x + 5}
                                        y={table.y + 5}
                                        fontSize={8}
                                        fill="white"
                                        lineHeight={1.2}
                                    />
                                    <Text
                                        text={`${table.seats} plekken`}
                                        x={table.x + 5}
                                        y={table.y + 20}
                                        fontSize={6}
                                        fill="white"
                                        lineHeight={1.2}
                                    />
                                </React.Fragment>
                            ))}
                        </Layer>
                    </Stage>
                </div>
            </div>

            <TableModal
                isOpen={modalOpen}
                onClose={() => setModalOpen(false)}
                table={modalTable}
                onTableAdded={handleTableAdded}
                setModalTable={setModalTable}
            />
        </div>
    );
};

export default TafelOpstelling;
